// TeamPage — раздел «Команда» (R40 / U-11, admin-only; маршрут /admin/team под RoleGuard).
// Таблица пользователей из GET /api/users (полный UserDto с ролями/статусом):
// поля username/displayName/email/phone/position/roles/status; статус локализован (U-16),
// показываются человекочитаемые подписи, а не UUID (U-15).
// «Создать пользователя» → диалог (username/email/password/displayName — контракт
// backend UserCreateRequest; назначение ролей/департаментов body POST не принимает, см. ТЗ §5.8
// → реализовано по фактическому контракту).
// Деактивация/активация → PUT /api/users/{id} {status} (UserUpdateRequest.status).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { usersApi, type UserCreateRequest, type UserDto } from '@/api/users';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** staleTime справочника пользователей (админ-таблица обновляется по мутациям). */
const USERS_ADMIN_STALE_TIME_MS = 5 * 60 * 1000;

/** Zod-схема формы создания пользователя (лимиты — ориентир backend). */
const createUserSchema = z.object({
  username: z.string().min(1, 'Имя пользователя обязательно').max(64, 'Слишком длинное имя'),
  email: z.string().min(1, 'Email обязателен').max(255, 'Слишком длинный email'),
  password: z.string().min(8, 'Пароль не короче 8 символов').max(128, 'Слишком длинный пароль'),
  displayName: z.string().min(1, 'Отображаемое имя обязательно').max(100, 'Слишком длинное имя'),
});

/** Значения формы создания пользователя. */
type CreateUserFormValues = z.infer<typeof createUserSchema>;

/** Локализованная подпись ролей: берем displayName роли (backend присылает готовую подпись). */
function rolesLabel(user: UserDto): string {
  if (user.roles.length === 0) return '—';
  return user.roles.map((r) => r.displayName || r.code).join(', ');
}

/** Локализованный статус пользователя (U-16; backend шлёт lowercase 'active'/'locked'/'disabled'). */
function statusLabel(status: string): string {
  return t(`users.status.${status}`);
}

/** Страница «Команда»: админ-реестр пользователей с созданием и сменой статуса. */
export function TeamPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: ['users', 'admin'],
    queryFn: () => usersApi.listAll(),
    staleTime: USERS_ADMIN_STALE_TIME_MS,
  });

  const createMutation = useMutation({
    mutationFn: (payload: UserCreateRequest) => usersApi.createUser(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateOpen(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      usersApi.updateUser(id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const { control, handleSubmit, formState } = useForm<CreateUserFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { username: '', email: '', password: '', displayName: '' },
  });

  const onCreateSubmit: SubmitHandler<CreateUserFormValues> = (values) => {
    createMutation.mutate(values);
  };

  return (
    <Box>
      <PageHeader
        title={t('team.title')}
        subtitle={t('team.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {t('team.create')}
          </Button>
        }
      />
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка пользователей…" />
      ) : query.isError ? (
        <EmptyState
          title={t('team.loadError')}
          description={query.error instanceof Error ? query.error.message : t('team.loadErrorDesc')}
        />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState title={t('team.empty')} description={t('team.emptyDesc')} />
      ) : (
        <Paper>
          {statusMutation.isError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {extractApiErrorMessage(statusMutation.error, t('team.operationError'))}
            </Alert>
          ) : null}
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>{t('team.columns.user')}</TableCell>
                  <TableCell>{t('team.columns.email')}</TableCell>
                  <TableCell>{t('team.columns.phone')}</TableCell>
                  <TableCell>{t('team.columns.position')}</TableCell>
                  <TableCell>{t('team.columns.roles')}</TableCell>
                  <TableCell>{t('team.columns.status')}</TableCell>
                  <TableCell>{t('team.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.displayName || user.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.phone ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.position ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rolesLabel(user)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(user.status)}
                        color={user.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {user.status === 'active' ? (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => statusMutation.mutate({ id: user.id, status: 'disabled' })}
                          disabled={statusMutation.isPending}
                        >
                          {t('team.deactivate')}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => statusMutation.mutate({ id: user.id, status: 'active' })}
                          disabled={statusMutation.isPending}
                        >
                          {t('team.activate')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('team.createTitle')}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onCreateSubmit)} noValidate>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {createMutation.isError ? (
                <Alert severity="error">
                  {extractApiErrorMessage(createMutation.error, t('team.createError'))}
                </Alert>
              ) : null}
              <Controller
                name="username"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('team.form.username')}
                    required
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('team.form.email')}
                    required
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('team.form.password')}
                    type="password"
                    required
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="displayName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('team.form.displayName')}
                    required
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>
            <DialogActions>
              <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={createMutation.isPending || !formState.isValid}
              >
                {t('team.createSubmit')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
